import React, { useEffect, useState } from 'react';
import {
  tutoringSubjectsV2Columns,
  getTutoringSubjectsV2ListData,
} from './getTutoringSubjectsV2ListData';
import { intialPageSize } from 'constants';
import ManageTutorsDialog from './ManageTutorsDialog';
import { useSelector, useDispatch } from 'react-redux';
import {
  getTutoringSubjectsV2,
  deleteTutoringSubjectV2,
  getTutoringSubjectsV2Tutors,
  getTutoringSubjectsV2TimeSlots,
} from 'layouts/tutoringV2/services/tutoringAndSubjectsV2Api';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import SubjectsAndTurorsV2Dialog from './SubjectsAndTurorsV2Dialog';
import { CreateNewSubjectButton } from '../../tutoringV2CommonUtils';
import { useNotification, pageSizeChangeHandler } from 'utils/commonUtils';
import { getSubjectsV2 } from 'layouts/tutoringV2/store/subjectsV2Slice/subjectsV2Slice';
import CommonDeleteDialog from 'components/Common/CommonDeleteDialog/CommonDeleteDialog';
import SubjectsAndTutorsTimeSlotsDialog from 'layouts/tutoring/components/tutoringTabs/components/subjectsAndTutorsTab/SubjectsAndTutorsTimeSlotsDialog';

function SubjectAndTutorsTabV2() {
  const { handleErrorResponse, NotificationPopup } = useNotification();

  const availableUsersList =
    useSelector((state) => state?.availableUsersList) || [];
  const subjects = useSelector((state) => state?.subjectsV2) || [];

  const dispatch = useDispatch();

  const [
    openSubjectsAndTutorsTimeSlotsDialog,
    setOpenSubjectsAndTutorsTimeSlotsDialog,
  ] = useState(false);
  const [timeSlotData, setTimeSlotData] = useState([]);
  const [newSubjectV2, setNewSubjectV2] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [tutoringSubjectV2Form, setTutoringSubjectV2Form] = useState({});
  const [openSubjectsAndTurorsV2Dialog, setOpenSubjectsAndTurorsV2Dialog] =
    useState(false);
  const [openManageTutorDialog, setOpenManageTutorDialog] = useState(false);
  const [tutoringSubjects, setTutoringSubjects] = useState({
    page: 1,
    rows: [],
    isLoading: false,
    pageSize: intialPageSize,
  });

  useEffect(() => {
    fetchTutoringSubjects();
  }, []);

  const fetchTutoringSubjects = async () => {
    try {
      setTutoringSubjects((prev) => ({
        ...prev,
        isLoading: true,
      }));

      const subjects = await getTutoringSubjectsV2();
      dispatch(getSubjectsV2(subjects));

      const tutorResponses = await Promise.all(
        subjects?.map((sub) => getTutoringSubjectsV2Tutors(sub?.subjectId)),
      );

      // Step 3: Build a quick lookup map { subjectId: tutorAssignments }
      const subjectTutorsMap = {};
      subjects?.forEach((sub, index) => {
        subjectTutorsMap[sub.subjectId] = tutorResponses[index];
      });

      const rows = getTutoringSubjectsV2ListData(
        subjects,
        subjectTutorsMap,
        availableUsersList,
      );

      setTutoringSubjects((prev) => ({
        ...prev,
        isLoading: false,
        rows,
      }));
    } catch (error) {
      setTutoringSubjects((prev) => ({
        ...prev,
        isLoading: false,
      }));
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const pageSizeChange = pageSizeChangeHandler(setTutoringSubjects);

  const handleEdit = (params) => {
    setTutoringSubjectV2Form(params?.row);
    setOpenSubjectsAndTurorsV2Dialog(true);
  };

  const handleDelete = (params) => {
    const subjectId = params?.row?.subjectId;
    setOpenDeleteDialog(true);
    setSelectedSubjectId(subjectId);
  };

  const handleViewTimeSlots = async (params) => {
    const subjectId = params?.row?.subjectId;
    try {
      setIsFormLoading(true);
      const data = await getTutoringSubjectsV2TimeSlots(subjectId);
      setTimeSlotData(data);
      setIsFormLoading(false);
      setOpenSubjectsAndTutorsTimeSlotsDialog(true);
    } catch (error) {
      setIsFormLoading(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleNewSubject = () => {
    setNewSubjectV2(true);
    setOpenSubjectsAndTurorsV2Dialog(true);
  };

  const handleManageTutors = (params) => {
    setSelectedSubject(params?.row);
    setOpenManageTutorDialog(true);
  };

  const formatTutorNames = (tutors, category, userMap) => {
    return tutors
      .filter((t) => t?.category === category)
      .map((t) => userMap?.get(t?.tutorId) || t?.tutorId)
      .join(', ');
  };

  const refreshSingleSubjectTutors = async (subjectId) => {
    try {
      const updatedTutors = await getTutoringSubjectsV2Tutors(subjectId);

      const userMap = new Map(
        availableUsersList?.map((u) => [
          u?.userId,
          `${u?.givenName} ${u?.familyName}`,
        ]),
      );

      const primaryTutors = formatTutorNames(updatedTutors, 'primary', userMap);
      const backupTutors = formatTutorNames(updatedTutors, 'backup', userMap);

      setTutoringSubjects((prev) => ({
        ...prev,
        rows: prev?.rows?.map((row) =>
          row.subjectId === subjectId
            ? { ...row, primaryTutors, backupTutors }
            : row,
        ),
      }));
    } catch (error) {
      handleErrorResponse(
        error?.response?.data?.message || 'Failed to refresh tutors',
      );
    }
  };

  const handleTutorsUpdated = () => {
    if (selectedSubject?.subjectId) {
      refreshSingleSubjectTutors(selectedSubject.subjectId);
    }
  };

  return (
    <>
      <CreateNewSubjectButton
        label="New Subject"
        handleClick={handleNewSubject}
      />
      <DeltaDataGrid
        listData={tutoringSubjects}
        setListData={pageSizeChange}
        columns={tutoringSubjectsV2Columns(
          handleEdit,
          handleDelete,
          handleViewTimeSlots,
          handleManageTutors,
        )}
      />
      <SubjectsAndTutorsTimeSlotsDialog
        timeSlotData={timeSlotData}
        isFormLoading={isFormLoading}
        open={openSubjectsAndTutorsTimeSlotsDialog}
        setOpen={setOpenSubjectsAndTutorsTimeSlotsDialog}
      />
      <SubjectsAndTurorsV2Dialog
        newSubject={newSubjectV2}
        setNewSubject={setNewSubjectV2}
        open={openSubjectsAndTurorsV2Dialog}
        setOpen={setOpenSubjectsAndTurorsV2Dialog}
        fetchTutoringSubjects={fetchTutoringSubjects}
        tutoringSubjectV2Form={tutoringSubjectV2Form}
        setTutoringSubjectV2Form={setTutoringSubjectV2Form}
      />
      {openManageTutorDialog && (
        <ManageTutorsDialog
          open={openManageTutorDialog}
          selectedSubject={selectedSubject}
          setOpen={setOpenManageTutorDialog}
          onTutorsUpdated={handleTutorsUpdated}
        />
      )}
      <CommonDeleteDialog
        snakBarName="Subject"
        fetchData={fetchTutoringSubjects}
        commonDeleteId={selectedSubjectId}
        openDeleteDialog={openDeleteDialog}
        commonDeleteApi={deleteTutoringSubjectV2}
        setOpenDeleteDialog={setOpenDeleteDialog}
        contentText="Are you sure you want to delete this subject ?"
      />
      <NotificationPopup />
    </>
  );
}

export default SubjectAndTutorsTabV2;
